import React from "react";
import Modal from "./Modal";
import { Calendar, Clock, Banknote } from "lucide-react";

interface UsageLimitsInfoModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const UsageLimitsInfoModal: React.FC<UsageLimitsInfoModalProps> = ({ isOpen, setIsOpen }) => {
  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="text-left">
        <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-1">
          How usage limits work
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          Your Individual plan includes three limits that work together.
        </p>

        <div className="space-y-4">
          {/* Daily limit */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#1d365d]/10 flex items-center justify-center">
              <Clock size={15} className="text-[#1d365d]" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">100 pages per day</p>
              <p className="text-sm text-gray-500 mt-0.5">
                You can upload up to 100 pages each day. This resets at midnight UTC. If you have pages saved in your rollover bank, they are added on top, so your effective daily limit can be higher than 100.
              </p>
            </div>
          </div>

          {/* Monthly limit */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#1d365d]/10 flex items-center justify-center">
              <Calendar size={15} className="text-[#1d365d]" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">3,000 pages per month</p>
              <p className="text-sm text-gray-500 mt-0.5">
                Across the entire billing period you can upload at most 3,000 pages. This resets automatically when your subscription renews each month.
              </p>
            </div>
          </div>

          {/* Rollover bank */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Banknote size={15} className="text-amber-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Rollover bank (up to 500 pages)</p>
              <p className="text-sm text-gray-500 mt-0.5">
                Any unused pages from your daily allowance carry over into a rollover bank, which holds up to 500 pages. Those banked pages are added to your daily limit, letting you upload more on busy days.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5 p-3.5 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-xs font-medium text-gray-700 mb-1">Example</p>
          <p className="text-xs text-gray-500 leading-relaxed">
            You upload nothing for 5 days, your bank fills to 500. On day 6 you can upload up to 600 pages in one go (100 daily + 500 bank), as long as you haven't exceeded 3,000 pages that month.
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UsageLimitsInfoModal;
